import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Globe, Receipt, Building2, ArrowRight, Smartphone, Lightbulb, Tv, Wifi, Zap, ArrowLeftRight, Banknote, CreditCard, Store, Heart, Target, GraduationCap, Stethoscope, Droplets, Home } from 'lucide-react';
export const PillarsSection: React.FC = () => {
  const pillars = [{
    id: 'crowdfunding',
    title: 'Community Crowdfunding',
    description: 'Zero fees for social causes. Raise funds for medical bills, community projects, and more.',
    icon: Users,
    color: 'bg-blue-50 text-blue-600',
    image: 'https://images.unsplash.com/photo-1509099836639-18ba1795216d?auto=format&fit=crop&w=800&q=80',
    customVisual: false as const
  }, {
    id: 'diaspora',
    title: 'Physical & Online Exchange',
    description: 'Exchange currencies in-store or online with competitive rates. Fast, secure, and transparent.',
    icon: Globe,
    color: 'bg-brand-primary/10 text-brand-primary',
    image: '',
    customVisual: 'diaspora' as const
  }, {
    id: 'bills',
    title: 'Unified Bill Payment',
    description: 'Manage all your utilities, airtime, and subscriptions from one single dashboard.',
    icon: Receipt,
    color: 'bg-orange-50 text-brand-accent',
    image: '',
    customVisual: 'bills' as const
  }, {
    id: 'facility',
    title: 'Facility Management',
    description: 'A dedicated hub for tenants and landlords to manage rent, maintenance, and leases.',
    icon: Building2,
    color: 'bg-purple-50 text-purple-600',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80',
    customVisual: false as const
  }];
  return <section id="solutions" className="py-24 bg-white border-t border-gray-100" data-id="element-159">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-160">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-161">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold text-brand-text-dark mb-4" data-id="element-162">
            Four Pillars. One Ecosystem.
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-brand-text-light" data-id="element-163">
            Explore the core features that make Billvast the ultimate financial
            companion for Nigerians everywhere.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-id="element-164">
          {pillars.map((pillar, index) => <motion.div key={pillar.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden ${pillar.id === 'crowdfunding' ? 'md:col-span-2' : ''}`} data-id="element-165">
              {pillar.customVisual === 'diaspora' ? <div className="relative h-48 overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50" style={{
            perspective: '800px'
          }} data-id="element-166">
                  <div className="absolute inset-0 flex items-center justify-center gap-5" style={{
              transformStyle: 'preserve-3d'
            }} data-id="element-167">
                    {/* Physical Store Exchange */}
                    <motion.div animate={{
                y: [0, -8, 0],
                rotateY: [0, 8, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-20 h-20 bg-white rounded-2xl shadow-xl shadow-green-200/50 flex flex-col items-center justify-center gap-1 border border-green-100" style={{
                transform: 'rotateX(5deg) rotateY(-5deg)'
              }} data-id="element-168">
                      <Store size={28} className="text-brand-primary" data-id="element-169" />
                      <span className="text-[10px] font-bold text-gray-600" data-id="element-170">
                        In-Store
                      </span>
                    </motion.div>

                    {/* Currency Exchange - Center */}
                    <motion.div animate={{
                y: [0, -12, 0],
                rotateY: [0, -6, 0]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.3
              }} className="w-24 h-24 bg-white rounded-2xl shadow-xl shadow-emerald-200/50 flex flex-col items-center justify-center gap-1.5 border border-emerald-100 -mt-4" style={{
                transform: 'rotateX(5deg) rotateY(5deg)'
              }} data-id="element-171">
                      <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center" data-id="element-172">
                        <ArrowLeftRight size={22} className="text-brand-primary" data-id="element-173" />
                      </div>
                      <div className="flex items-center gap-1" data-id="element-174">
                        <span className="text-[9px] font-bold text-brand-primary" data-id="element-175">
                          USD
                        </span>
                        <span className="text-[9px] text-gray-400" data-id="element-176">⇄</span>
                        <span className="text-[9px] font-bold text-brand-primary-dark" data-id="element-177">
                          NGN
                        </span>
                      </div>
                    </motion.div>

                    {/* Online / Card Exchange */}
                    <motion.div animate={{
                y: [0, -6, 0],
                rotateY: [0, 10, 0]
              }} transition={{
                duration: 2.8,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.6
              }} className="w-20 h-20 bg-white rounded-2xl shadow-xl shadow-teal-200/50 flex flex-col items-center justify-center gap-1 border border-teal-100" style={{
                transform: 'rotateX(5deg) rotateY(-8deg)'
              }} data-id="element-178">
                      <CreditCard size={28} className="text-teal-600" data-id="element-179" />
                      <span className="text-[10px] font-bold text-gray-600" data-id="element-180">
                        Online
                      </span>
                    </motion.div>
                  </div>

                  {/* Floating accent icons */}
                  <motion.div animate={{
              y: [0, -5, 0],
              x: [0, 3, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-3 right-6 w-10 h-10 bg-white/80 rounded-xl shadow-md flex items-center justify-center border border-green-100" data-id="element-181">
                    <Banknote size={16} className="text-brand-primary-dark" data-id="element-182" />
                  </motion.div>

                  <motion.div animate={{
              y: [0, -4, 0],
              x: [0, -2, 0]
            }} transition={{
              duration: 3.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.5
            }} className="absolute bottom-3 left-6 w-10 h-10 bg-white/80 rounded-xl shadow-md flex items-center justify-center border border-emerald-100" data-id="element-183">
                    <Globe size={16} className="text-emerald-500" data-id="element-184" />
                  </motion.div>

                  {/* Pillar icon badge */}
                  <div className={`absolute top-4 left-4 w-12 h-12 rounded-2xl flex items-center justify-center backdrop-blur-sm bg-white/90 ${pillar.color}`} data-id="element-185">
                    <pillar.icon size={24} data-id="element-186" />
                  </div>
                </div> : pillar.customVisual === 'bills' ? <div className="relative h-48 overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50" style={{
            perspective: '800px'
          }} data-id="element-187">
                  {/* 3D floating utility icons */}
                  <div className="absolute inset-0 flex items-center justify-center gap-6" style={{
              transformStyle: 'preserve-3d'
            }} data-id="element-188">
                    {/* Airtime / Phone */}
                    <motion.div animate={{
                y: [0, -8, 0],
                rotateY: [0, 10, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-20 h-20 bg-white rounded-2xl shadow-xl shadow-orange-200/50 flex flex-col items-center justify-center gap-1 border border-orange-100" style={{
                transform: 'rotateX(5deg) rotateY(-5deg)'
              }} data-id="element-189">
                      <Smartphone size={28} className="text-brand-accent" data-id="element-190" />
                      <span className="text-[10px] font-bold text-gray-600" data-id="element-191">
                        Airtime
                      </span>
                    </motion.div>

                    {/* Electricity */}
                    <motion.div animate={{
                y: [0, -12, 0],
                rotateY: [0, -8, 0]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.3
              }} className="w-24 h-24 bg-white rounded-2xl shadow-xl shadow-yellow-200/50 flex flex-col items-center justify-center gap-1 border border-yellow-100 -mt-4" style={{
                transform: 'rotateX(5deg) rotateY(5deg)'
              }} data-id="element-192">
                      <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-193">
                        <Zap size={24} className="text-yellow-500" data-id="element-194" />
                      </div>
                      <span className="text-[10px] font-bold text-gray-600" data-id="element-195">
                        Electricity
                      </span>
                    </motion.div>

                    {/* TV / Subscriptions */}
                    <motion.div animate={{
                y: [0, -6, 0],
                rotateY: [0, 12, 0]
              }} transition={{
                duration: 2.8,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.6
              }} className="w-20 h-20 bg-white rounded-2xl shadow-xl shadow-purple-200/50 flex flex-col items-center justify-center gap-1 border border-purple-100" style={{
                transform: 'rotateX(5deg) rotateY(-8deg)'
              }} data-id="element-196">
                      <Tv size={28} className="text-purple-500" data-id="element-197" />
                      <span className="text-[10px] font-bold text-gray-600" data-id="element-198">
                        TV/Cable
                      </span>
                    </motion.div>
                  </div>

                  {/* Floating small icons for depth */}
                  <motion.div animate={{
              y: [0, -5, 0],
              x: [0, 3, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-3 right-6 w-10 h-10 bg-white/80 rounded-xl shadow-md flex items-center justify-center border border-green-100" data-id="element-199">
                    <Wifi size={16} className="text-brand-primary" data-id="element-200" />
                  </motion.div>

                  <motion.div animate={{
              y: [0, -4, 0],
              x: [0, -2, 0]
            }} transition={{
              duration: 3.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.5
            }} className="absolute bottom-3 left-6 w-10 h-10 bg-white/80 rounded-xl shadow-md flex items-center justify-center border border-blue-100" data-id="element-201">
                    <Lightbulb size={16} className="text-blue-400" data-id="element-202" />
                  </motion.div>

                  {/* Pillar icon badge */}
                  <div className={`absolute top-4 left-4 w-12 h-12 rounded-2xl flex items-center justify-center backdrop-blur-sm bg-white/90 ${pillar.color}`} data-id="element-203">
                    <pillar.icon size={24} data-id="element-204" />
                  </div>
                </div> : <div className="relative h-48 overflow-hidden" data-id="element-205">
                  <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-206" />
                  <div className="absolute inset-0 bg-black/20" data-id="element-207" />
                  <div className={`absolute top-4 left-4 w-12 h-12 rounded-2xl flex items-center justify-center backdrop-blur-sm bg-white/90 ${pillar.color}`} data-id="element-208">
                    <pillar.icon size={24} data-id="element-209" />
                  </div>
                </div>}
              <div className="p-8" data-id="element-210">
                <h3 className="text-2xl font-bold text-brand-text-dark mb-3" data-id="element-211">
                  {pillar.title}
                </h3>
                <p className="text-brand-text-light mb-8 leading-relaxed" data-id="element-212">
                  {pillar.description}
                </p>
                <div className="flex items-center text-brand-primary font-semibold group-hover:gap-2 transition-all" data-id="element-213">
                  Learn More <ArrowRight size={18} className="ml-1" data-id="element-214" />
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};